/**
 * KB Ingest — mirrors apps/experts/public/admin/kb-ingest.html. Two halves:
 *   1. A KB-stats panel (GET /api/admin/kb-stats) — per-doc chunk + retrieval
 *      stats with hot/dead tagging (the command-center kb tile, full view).
 *   2. PDF → markdown → PR: upload a PDF (multipart, see ./data-access/kb-upload),
 *      review/edit the generated markdown, then open a GitHub PR
 *      (POST /api/admin/kb/upload?action=pr, JSON via the api client).
 *
 * Client-side guards (PDF type + 20 MB) mirror the original before the upload
 * fires, so an obviously-wrong file fails fast without a round-trip.
 */
import { useState } from 'react';
import { Textarea } from '@andavo/web-ui';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  StatusDot,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/primitives';
import {
  useCreateKbPr,
  useKbStats,
  useUploadKbPdf,
} from '../../data-access/useAdmin';
import { relTime } from '../../lib/format';

const MAX_PDF_BYTES = 20 * 1024 * 1024;

function KbStatsPanel() {
  const { data, isPending, isError, error } = useKbStats();
  if (isPending) return <AdminLoading />;
  if (isError) {
    return (
      <AdminError
        message={`Failed to load KB stats: ${(error as Error)?.message ?? 'unknown'}`}
      />
    );
  }
  if (!data) return <AdminEmpty>No stats.</AdminEmpty>;

  const t = data.totals;
  return (
    <div>
      <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-1.5 font-inter text-sm sm:grid-cols-5">
        <Stat label="Docs" value={t.docs} />
        <Stat label="Chunks" value={t.chunks} />
        <Stat label="Searches 7d" value={t.searches_7d} />
        <Stat label="Hot" value={t.hot_articles} />
        <Stat label="Dead" value={t.dead_articles} />
      </dl>
      <Table
        head={
          <>
            <Th>Path</Th>
            <Th>Chunks</Th>
            <Th>Retrievals 7d</Th>
            <Th>Avg score</Th>
            <Th>Last</Th>
          </>
        }
      >
        {data.docs.slice(0, 20).map((doc) => (
          <Tr key={doc.path}>
            <Td>
              <span className="inline-flex items-center gap-1.5">
                <StatusDot
                  tone={doc.is_hot ? 'ok' : doc.is_dead ? 'muted' : 'warn'}
                />
                <Mono title={doc.path}>
                  {doc.path.replace('knowledge/', '')}
                </Mono>
              </span>
            </Td>
            <Td>{doc.chunks}</Td>
            <Td>{doc.retrievals_7d}</Td>
            <Td>{doc.mean_score != null ? doc.mean_score.toFixed(3) : '—'}</Td>
            <Td>{relTime(doc.last_retrieved)}</Td>
          </Tr>
        ))}
      </Table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-content-tertiary text-xs">{label}</dt>
      <dd className="font-sans font-semibold text-content-primary">{value}</dd>
    </div>
  );
}

export function KbIngestPage() {
  const upload = useUploadKbPdf();
  const pr = useCreateKbPr();
  const [markdown, setMarkdown] = useState('');
  const [slug, setSlug] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  function onFile(file: File | undefined) {
    setLocalError(null);
    pr.reset();
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setLocalError(`Expected a PDF — got ${file.type || 'unknown type'}.`);
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setLocalError('File exceeds the 20 MB limit.');
      return;
    }
    upload.mutate(file, {
      onSuccess: (res) => {
        setMarkdown(res.markdown);
        setSlug(res.slug);
      },
    });
  }

  const uploadError = upload.isError ? (upload.error as Error)?.message : null;

  return (
    <div>
      <AdminPageHeader
        subtitle="Knowledge-base stats, and PDF → markdown → PR ingestion."
        title="KB Ingest"
      />

      <Panel title="Knowledge base stats">
        <KbStatsPanel />
      </Panel>

      <Panel className="mt-4" title="Ingest a PDF">
        <div className="flex flex-col gap-4">
          <div>
            <label
              className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border-subtle border-dashed px-4 py-3 font-inter text-content-secondary text-sm hover:bg-surface-background"
              htmlFor="kb-pdf-input"
            >
              {upload.isPending ? 'Extracting…' : 'Choose a PDF to ingest'}
            </label>
            <input
              accept="application/pdf"
              className="hidden"
              disabled={upload.isPending}
              id="kb-pdf-input"
              onChange={(e) => onFile(e.target.files?.[0])}
              type="file"
            />
          </div>

          {localError ? <AdminError message={localError} /> : null}
          {uploadError ? <AdminError message={uploadError} /> : null}

          {markdown ? (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
                Slug
                <input
                  className="rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 font-mono text-content-primary text-sm"
                  onChange={(e) => setSlug(e.target.value)}
                  value={slug}
                />
              </label>
              <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
                Generated markdown (review &amp; edit before opening a PR)
                <Textarea
                  className="min-h-64 font-mono text-xs"
                  onChange={(e) => setMarkdown(e.target.value)}
                  value={markdown}
                />
              </label>

              <div className="flex items-center gap-2">
                <button
                  className="rounded-md bg-surface-action-subtle px-3 py-1.5 font-inter text-content-action text-sm hover:opacity-90 disabled:opacity-50"
                  disabled={pr.isPending || !markdown.trim() || !slug.trim()}
                  onClick={() =>
                    pr.mutate({ markdown: markdown.trim(), slug: slug.trim() })
                  }
                  type="button"
                >
                  {pr.isPending ? 'Opening PR…' : 'Save as PR'}
                </button>
                <button
                  className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
                  onClick={() => {
                    setMarkdown('');
                    setSlug('');
                    setLocalError(null);
                    pr.reset();
                  }}
                  type="button"
                >
                  Reset
                </button>
              </div>

              {pr.isError ? (
                <AdminError
                  message={`PR creation failed: ${(pr.error as Error)?.message ?? 'unknown'}`}
                />
              ) : null}
              {pr.isSuccess && pr.data?.prUrl ? (
                <div className="rounded-md border border-green-500/40 bg-green-500/5 px-3 py-2 font-inter text-green-700 text-sm">
                  PR opened:{' '}
                  <a
                    className="underline"
                    href={pr.data.prUrl}
                    rel="noreferrer"
                    target="_blank"
                  >
                    #{pr.data.prNumber} ({pr.data.branch})
                  </a>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

export default KbIngestPage;
